// useOpenDatabaseSettingsModal.js

import { useCallback, useEffect, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

import useBaseAccess from 'hooks/useBaseAccess';
import useDatabaseSchemaState from 'collection/hooks/useDatabaseSchemaState';
import { SETTINGS_TAB_ABOUT } from 'collection/settings/databaseSettingsTabs';
import { getCollection } from 'services/collections';
import { isLazySystemDatabaseType } from 'utilities/lazySystemDatabaseTypes';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function isSystemTypeDef(typeDef, typeKey = '') {
  if (typeDef?.system === true || typeDef?.isSystem === true) return true;
  return isLazySystemDatabaseType(typeKey);
}

const noop = () => {};

/**
 * Opens DatabaseModal for a collection by key (Settings Collections, AI, etc.).
 *
 * By default allows system types (views / workflows). Pass `allowSystemTypes: false`
 * to match chat-only reconfigure (custom types only).
 *
 * @param {object} [options]
 * @param {boolean} [options.allowSystemTypes=true]
 * @returns {object}
 */
export default function useOpenDatabaseSettingsModal({
  allowSystemTypes = true,
} = {}) {
  const language = useCurrentLanguage();
  const { user } = useUser();
  const { activeBase } = useBase();
  const { can, isRoleLoading } = useBaseAccess();
  const { getAll, getCount, getRevision, getType } = useNodeTypes();

  const [isOpen, setIsOpen] = useState(false);
  const [activeDatabase, setActiveDatabase] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(null);
  const [initialTab, setInitialTab] = useState(SETTINGS_TAB_ABOUT);
  const [viewEditorRequest, setViewEditorRequest] = useState(null);
  const [workflowEditorRequest, setWorkflowEditorRequest] = useState(null);
  const [isOpening, setIsOpening] = useState(false);

  const typeKey = normalizeString(activeDatabase?.type || pendingOpen?.typeKey);
  const typeDef = typeKey ? getType(typeKey) : null;
  const baseKey = normalizeString(
    activeDatabase?.baseKey
    || pendingOpen?.baseKey
    || activeBase?.key,
  ) || null;
  const userKey = user?.key || null;
  const canManageTypeSchema = !isRoleLoading && can('types.update');
  const isSystemType = isSystemTypeDef(typeDef, typeKey);

  const {
    nodeTypeOptions,
    resolvedProperties,
  } = useDatabaseSchemaState({
    typeKey,
    nodeTypeCount: getCount(),
    nodeTypesRevision: getRevision(),
    getAll,
    language,
  });

  /**
   * Whether settings can be opened for a type (mirrors Database.jsx gates).
   *
   * @param {string} [actionTypeKey]
   * @param {object|null} [actionTypeDef]
   * @returns {boolean}
   */
  const canOpenSettingsForType = useCallback((actionTypeKey = '', actionTypeDef = null) => {
    if (isRoleLoading) return false;
    const cleanTypeKey = normalizeString(actionTypeKey);
    const resolved = actionTypeDef || (cleanTypeKey ? getType(cleanTypeKey) : null);
    const system = isSystemTypeDef(resolved, cleanTypeKey);

    if (system) {
      if (!allowSystemTypes) return false;
      // System collections: views/workflows for anyone who can manage base views
      // (same spirit as Database canOpenDatabaseSettings).
      return true;
    }

    return canManageTypeSchema;
  }, [allowSystemTypes, canManageTypeSchema, getType, isRoleLoading]);

  const openDatabaseSettings = useCallback(async ({
    databaseKey,
    database: seedDatabase = null,
    typeKey: actionTypeKey = '',
    baseKey: actionBaseKey = '',
    initialTab: requestedInitialTab = SETTINGS_TAB_ABOUT,
    viewEditorRequest: nextViewEditorRequest = null,
    workflowEditorRequest: nextWorkflowEditorRequest = null,
  } = {}) => {
    const cleanDatabaseKey = normalizeString(databaseKey || seedDatabase?.key);
    if (!cleanDatabaseKey) return;

    const cleanActionTypeKey = normalizeString(
      actionTypeKey || seedDatabase?.type || '',
    );
    if (cleanActionTypeKey && !canOpenSettingsForType(cleanActionTypeKey)) {
      toast.error('You do not have permission to open settings for this collection.');
      return;
    }

    setIsOpening(true);

    try {
      const database = seedDatabase?.key === cleanDatabaseKey
        ? seedDatabase
        : await getCollection(cleanDatabaseKey);

      if (!database) {
        toast.error('Collection not found.');
        return;
      }

      const resolvedTypeKey = normalizeString(actionTypeKey || database.type);
      if (!canOpenSettingsForType(resolvedTypeKey)) {
        toast.error('You do not have permission to open settings for this collection.');
        return;
      }

      setPendingOpen({
        database,
        typeKey: resolvedTypeKey,
        baseKey: normalizeString(actionBaseKey || database.baseKey),
        viewEditorRequest: nextViewEditorRequest && typeof nextViewEditorRequest === 'object'
          ? nextViewEditorRequest
          : null,
        workflowEditorRequest: nextWorkflowEditorRequest && typeof nextWorkflowEditorRequest === 'object'
          ? nextWorkflowEditorRequest
          : null,
      });
      setInitialTab(requestedInitialTab);
    } catch (error) {
      console.error('[DatabaseSettings] Failed to load collection', error);
      toast.error('Could not open collection settings.');
    } finally {
      setIsOpening(false);
    }
  }, [canOpenSettingsForType]);

  useEffect(() => {
    if (!pendingOpen?.typeKey) return undefined;

    const resolvedTypeDef = getType(pendingOpen.typeKey);
    if (resolvedTypeDef) {
      if (!canOpenSettingsForType(pendingOpen.typeKey, resolvedTypeDef)) {
        toast.error('You do not have permission to open settings for this collection.');
        setPendingOpen(null);
        return undefined;
      }
      setActiveDatabase(pendingOpen.database);
      setViewEditorRequest(pendingOpen.viewEditorRequest || null);
      setWorkflowEditorRequest(pendingOpen.workflowEditorRequest || null);
      setIsOpen(true);
      setPendingOpen(null);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setPendingOpen((current) => {
        if (!current?.typeKey || getType(current.typeKey)) return current;
        toast.error('Collection type is still loading. Try again in a moment.');
        return null;
      });
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [canOpenSettingsForType, getRevision, getType, pendingOpen]);

  useEffect(() => {
    if (isOpen) return;
    setActiveDatabase(null);
    setPendingOpen(null);
    setViewEditorRequest(null);
    setWorkflowEditorRequest(null);
  }, [isOpen]);

  const handleViewEditorRequestHandled = useCallback(() => {
    setViewEditorRequest(null);
  }, []);

  const handleWorkflowEditorRequestHandled = useCallback(() => {
    setWorkflowEditorRequest(null);
  }, []);

  const databaseModalProps = useMemo(() => ({
    activeDatabase,
    canPublishSchema: false,
    initialTab,
    isOpen,
    nodeTypeOptions,
    onAutomationsSaved: noop,
    onExport: noop,
    onOpenImport: noop,
    onPublishSchema: noop,
    onSelectView: noop,
    onViewEditorRequestHandled: handleViewEditorRequestHandled,
    onWorkflowEditorRequestHandled: handleWorkflowEditorRequestHandled,
    previewNodeKeyOverride: '',
    publishingSchema: false,
    resolvedProperties,
    selectedViewId: null,
    setOpen: setIsOpen,
    typeDef,
    typeKey,
    userKey,
    viewEditorRequest,
    workflowEditorRequest,
    views: [],
    baseKey,
  }), [
    activeDatabase,
    handleViewEditorRequestHandled,
    handleWorkflowEditorRequestHandled,
    initialTab,
    isOpen,
    nodeTypeOptions,
    resolvedProperties,
    typeDef,
    typeKey,
    userKey,
    viewEditorRequest,
    workflowEditorRequest,
    baseKey,
  ]);

  return {
    canManageTypeSchema,
    canOpenSettingsForType,
    databaseModalProps,
    isOpening,
    isSystemType,
    openDatabaseSettings,
  };
}
